import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ChecklistPreview } from '@/products/maintenance/features/protocol-renderer/checklist-preview';
import { maintenanceTemplateVersionLabel } from '@/products/maintenance/features/maintenance-status';
import { TemplateVersionEditor } from '@/products/maintenance/features/templates/template-version-editor';
import {
  templateKindLabel,
  templateNeedsPdf,
  templatePdfPreviewText,
} from '@/products/maintenance/features/templates/template-kind-label';
import { PdfOverlayEditor } from '@/products/maintenance/features/templates/pdf-overlay/pdf-overlay-editor';
import {
  maintenanceTemplateSourcePdfUrl,
  useMaintenanceTemplate,
  useReopenMaintenanceTemplateVersion,
  useRetireMaintenanceTemplateVersion,
  useUploadMaintenanceTemplatePdf,
} from '@/products/maintenance/features/use-maintenance-templates';
import { maintenanceTemplatePath } from '@/products/maintenance/maintenance-paths';

export function MaintenanceTemplateVersionPage() {
  const copy = useVoiceCopy();
  const { templateId = '', versionId = '' } = useParams();
  const template = useMaintenanceTemplate(templateId);
  const upload = useUploadMaintenanceTemplatePdf(templateId);
  const reopen = useReopenMaintenanceTemplateVersion(templateId);
  const retire = useRetireMaintenanceTemplateVersion(templateId);
  const [file, setFile] = useState<File | null>(null);
  const [confirm, setConfirm] = useState<'reopen' | 'retire' | null>(null);

  const version = template.data?.versions.find((v) => v.id === versionId);
  const templateRow = template.data;
  usePageTitle(version ? `Version ${version.versionNumber}` : 'Version');

  async function onUpload(event: FormEvent) {
    event.preventDefault();
    if (!file || !version) {
      return;
    }
    try {
      const uploaded = await upload.mutateAsync({ versionId: version.id, file });
      toast.success('PDF hochgeladen.');
      if (uploaded.unsupportedWidgets.length > 0) {
        const names = uploaded.unsupportedWidgets.map((widget) => widget.name).join(', ');
        toast.warning(`Nicht übernommen: ${names}`);
      }
      setFile(null);
    } catch {
      toast.error('Das PDF konnte nicht hochgeladen werden.');
    }
  }

  if (template.isPending) {
    return <p className="text-sm text-muted-foreground">Version wird geladen …</p>;
  }
  if (!version || !templateRow) {
    return <p className="text-sm text-destructive">Die Version wurde nicht gefunden.</p>;
  }

  const canEdit = version.status === 'draft';
  const canReopen = version.status === 'published' && !version.inUse;
  const canRetire = version.status === 'published';
  const pdfUrl =
    version.hasSourcePdf && version.sourcePdfScanStatus !== 'PENDING' && version.pageCount
      ? maintenanceTemplateSourcePdfUrl(version.id)
      : null;

  async function confirmAction() {
    if (!version) {
      return;
    }
    const action = confirm === 'reopen' ? reopen : retire;
    try {
      await action.mutateAsync(version.id);
      toast.success(
        confirm === 'reopen' ? 'Version ist wieder ein Entwurf.' : 'Version zurückgezogen.',
      );
      setConfirm(null);
    } catch (err: unknown) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Die Aktion konnte nicht ausgeführt werden.';
      toast.error(message);
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title={`Version ${version.versionNumber}`}
        description={`Status: ${maintenanceTemplateVersionLabel(version.status)} · ${templateKindLabel(templateRow.kind)}`}
      />
      <p className="text-sm">
        <Link className="text-primary hover:underline" to={maintenanceTemplatePath(templateId)}>
          ← Zur Vorlage
        </Link>
      </p>

      {canReopen || canRetire ? (
        <div className="flex flex-wrap gap-2">
          {canReopen ? (
            <Button type="button" onClick={() => setConfirm('reopen')}>
              Als Entwurf bearbeiten
            </Button>
          ) : null}
          {canRetire ? (
            <Button type="button" variant="outline" onClick={() => setConfirm('retire')}>
              Zurückziehen
            </Button>
          ) : null}
        </div>
      ) : null}

      {version.inUse ? (
        <p className="text-sm text-muted-foreground">
          {copy('maintenance.template.version.locked')}
        </p>
      ) : null}

      {canEdit && templateRow.kind !== 'structured' ? (
        <form
          className="grid max-w-xl gap-3 rounded-md border p-4"
          onSubmit={(e) => void onUpload(e)}
        >
          <h2 className="text-base font-semibold">Quell-PDF</h2>
          <div className="space-y-1">
            <Label htmlFor="pdf-file">Datei</Label>
            <Input
              id="pdf-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <Button type="submit" disabled={!file || upload.isPending} className="w-fit">
            PDF hochladen
          </Button>
        </form>
      ) : null}

      {canEdit ? (
        <TemplateVersionEditor
          key={`${version.id}-${JSON.stringify(version.definition)}`}
          template={templateRow}
          version={version}
        />
      ) : templateNeedsPdf(templateRow.kind) ? (
        pdfUrl && version.pageCount ? (
          <section className="space-y-2">
            <h2 className="text-base font-semibold">PDF</h2>
            <PdfOverlayEditor
              pdfUrl={pdfUrl}
              definition={version.definition}
              pageCount={version.pageCount}
              selectedFieldKey={null}
              onSelectField={() => undefined}
            />
          </section>
        ) : (
          <p className="text-sm text-muted-foreground">
            {templatePdfPreviewText('version', version.hasSourcePdf)}
          </p>
        )
      ) : (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Vorschau</h2>
          <ChecklistPreview definition={version.definition} />
        </section>
      )}

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'reopen' ? 'Version wieder als Entwurf?' : 'Version zurückziehen?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'reopen'
                ? 'Die Quell-PDF bleibt erhalten. Neue Aufträge sehen diese Version erst nach erneutem Veröffentlichen.'
                : 'Neue Aufträge können diese Version danach nicht mehr wählen. Bereits angelegte Protokolle bleiben unverändert.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopen.isPending || retire.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={reopen.isPending || retire.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmAction();
              }}
            >
              {confirm === 'reopen' ? 'Als Entwurf' : 'Zurückziehen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
